import React, { useEffect, useState } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { isPlatformCreator } from '../lib/exploreTemplates';
import NotificationBell from './NotificationBell';
import UserMenu from './UserMenu';
import GenerationCompletionToasts from './GenerationCompletionToasts';
import { GenerationToastProvider } from '../contexts/GenerationToastContext';
import { homePathForRole } from '../lib/homeRoute';
import { ALL_MODULES, TOOL_MODULES, modulesForRole } from '../lib/appModules';

/**
 * App shell: a floating pill of creation tools on the left edge, the profile menu (account, billing,
 * management, sign-out) at the top right, and the page content taking the whole width.
 */
export default function Layout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { org, role } = useOrg();
  const location = useLocation();
  const [isCreator, setIsCreator] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsCreator(false);
      return;
    }
    let cancel = false;
    isPlatformCreator(user.id).then((ok) => {
      if (!cancel) setIsCreator(ok);
    });
    return () => { cancel = true; };
  }, [user]);

  const planName = org?.plans?.name || '';
  const isAutonomous = planName.toLowerCase() === 'individual';
  const tools = modulesForRole(TOOL_MODULES, role, isAutonomous, isCreator);

  const currentPage = ALL_MODULES.find((m) => location.pathname === m.id || location.pathname.startsWith(`${m.id}/`));
  // These screens already print the module name as their own heading (or a back link).
  // The shell label would repeat it, as in "Explorar" in the bar and again in the page.
  const headingInPage = new Set([
    '/explorar',
    '/estudio',
    '/mobiliacao',
    '/galeria',
    '/kanban',
    '/marketplace',
    '/conta',
    '/app',
    '/gerenciamento/usuarios',
    '/financeiro',
    '/pagamento',
    '/solicitacoes',
    '/tags',
    '/indicacoes',
  ]);
  const isBlankCanvas = location.pathname === '/tela-em-branco';
  const fullBleed = location.pathname === '/editor' || isBlankCanvas;
  /** Explorar sticky filters sit flush under the header — no content-wrapper top padding. */
  const stickyFlushTop = location.pathname === '/explorar';

  return (
    <GenerationToastProvider>
    <div className="dashboard-layout">
      <a href="#main-content" className="skip-link">Pular para o conteúdo</a>
      {/* Floating tools pill */}
      <aside className="sidebar" aria-label="Ferramentas">
        <Link to={homePathForRole(role)} className="sidebar-logo" title="Início" aria-label="Início">
          <img src="/assets/icon-mark.png" alt="" className="logo-mark" />
        </Link>
        <nav className="sidebar-nav" aria-label="Módulos">
          {tools.map((mod) => {
            const isActive = location.pathname === mod.id || location.pathname.startsWith(`${mod.id}/`);
            return (
              <Link
                key={mod.id}
                to={mod.id}
                className={`nav-item ${isActive ? 'active' : ''}`}
                data-title={mod.title}
                aria-label={mod.title}
                aria-current={isActive ? 'page' : undefined}
              >
                {mod.icon}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="main-content" id="main-content" tabIndex={-1}>
        {isBlankCanvas ? (
          <div className="floating-user-menu">
            <NotificationBell />
            <UserMenu floating />
          </div>
        ) : (
          <header className="desktop-header" role="banner">
            <div className="desktop-header-left">
              {currentPage && !headingInPage.has(currentPage.id) && (
                <div className="breadcrumb">
                  {currentPage.icon}
                  <span>{currentPage.title}</span>
                </div>
              )}
            </div>
            <div className="desktop-header-right">
              <NotificationBell />
              <UserMenu />
            </div>
          </header>
        )}

        <div className={`content-wrapper${fullBleed ? ' content-wrapper--editor' : ''}${stickyFlushTop ? ' content-wrapper--sticky-top' : ''}`}>
          {children}
        </div>
      </main>
      <GenerationCompletionToasts />
    </div>
    </GenerationToastProvider>
  );
}
